import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BookmarkButton from './BookmarkButton';
import { useLibrary } from '../context/LibraryContext';

// Generate a deterministic gradient from a string
function stringToGradient(str = '') {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  const gradients = [
    ['#1a1a4e', '#2d1b69'],
    ['#1a2e4e', '#16213e'],
    ['#2e1a1a', '#4e1a2d'],
    ['#1a2e1a', '#1a4e2e'],
    ['#2e2e1a', '#4e3a1a'],
    ['#1a2e2e', '#1a3a4e'],
    ['#2e1a2e', '#4e1a3a'],
    ['#1a1a1a', '#2e1a4e'],
  ];

  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
}

// Get initials (up to 2 chars) from a title
function getInitials(title = '') {
  const words = title.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function BookCoverPlaceholder({ title }) {
  const [from, to] = stringToGradient(title);
  const initials = getInitials(title);

  return (
    <div
      className="w-full h-full flex items-center justify-center"
      style={{
        background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
      }}
    >
      <div className="text-center px-2">
        <span className="font-display text-4xl font-bold text-white/80 block">{initials}</span>
        <div
          className="mt-2 w-8 mx-auto border-t border-white/20"
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

// Status dot config
const STATUS_DOT = {
  want: { color: '#6366f1', title: 'Want to Read' },
  reading: { color: '#f5a623', title: 'Currently Reading' },
  finished: { color: '#22c55e', title: 'Finished' },
};

function BookCard({ book, className = '' }) {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [hovered, setHovered] = useState(false);

  // useLibrary is safe to call here because BookCard is always rendered
  // inside the LibraryProvider (wrapped in App)
  const { isBookmarked, getReadingStatus } = useLibrary();

  if (!book) return null;

  const { id, title, authors, cover, downloadCount } = book;

  const hasCover = cover && !imgError;
  const detailUrl = `/book/${id}`;
  const bookmarked = isBookmarked(id);
  const status = getReadingStatus(id);
  const statusConfig = status ? STATUS_DOT[status] : null;

  return (
    <article
      className={`card group flex flex-col overflow-hidden animate-fade-in ${className}`}
      aria-label={`${title} by ${authors || 'Unknown Author'}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Cover Image */}
      <Link to={detailUrl} className="block relative overflow-hidden" style={{ paddingTop: '140%' }}>
        <div className="absolute inset-0">
          {hasCover ? (
            <>
              {!imgLoaded && (
                <div className="absolute inset-0 skeleton-shimmer" />
              )}
              <img
                src={cover}
                alt={`Cover of ${title}`}
                className={`w-full h-full object-cover transition-all duration-500
                            group-hover:scale-105 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImgLoaded(true)}
                onError={() => setImgError(true)}
                loading="lazy"
              />
            </>
          ) : (
            <div className="w-full h-full transition-transform duration-500 group-hover:scale-105">
              <BookCoverPlaceholder title={title} />
            </div>
          )}

          {/* Gradient overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent
                          opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* ── Bookmark button overlay (top-right) ── */}
          <div
            className="absolute top-2 right-2 transition-opacity duration-200"
            style={{ opacity: hovered || bookmarked ? 1 : 0 }}
            // Prevent the Link from triggering when clicking bookmark
          >
            <BookmarkButton book={book} size="sm" />
          </div>

          {/* ── Reading status dot (bottom of cover) ── */}
          {statusConfig && (
            <div className="absolute bottom-2 left-2">
              <div
                className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium
                           backdrop-blur-sm"
                style={{
                  background: `${statusConfig.color}30`,
                  border: `1px solid ${statusConfig.color}60`,
                  color: statusConfig.color,
                }}
                title={statusConfig.title}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: statusConfig.color }}
                />
                <span className="hidden group-hover:inline">{statusConfig.title}</span>
              </div>
            </div>
          )}
        </div>
      </Link>

      {/* Card Body */}
      <div className="flex flex-col flex-1 p-4">
        {/* Download count */}
        {downloadCount > 0 && (
          <div className="flex items-center justify-end mb-2">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {downloadCount.toLocaleString()}
            </span>
          </div>
        )}

        {/* Title */}
        <Link to={detailUrl} className="group/title flex-1">
          <h3
            className="font-display font-semibold text-white text-sm leading-snug
                       line-clamp-2 group-hover/title:text-accent transition-colors duration-200 mb-1"
            title={title}
          >
            {title}
          </h3>
        </Link>

        {/* Author */}
        <p className="text-gray-400 text-xs mt-1 line-clamp-1" title={authors}>
          {authors || 'Unknown Author'}
        </p>

        {/* CTA Button */}
        <Link
          to={detailUrl}
          className="mt-4 btn-primary text-xs py-2 w-full"
          aria-label={`Read ${title} for free`}
        >
          <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477
                 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5
                 c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18
                 c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          Read Free
        </Link>
      </div>
    </article>
  );
}

export default BookCard;
export { BookCoverPlaceholder, getInitials, stringToGradient };
